/**
 * Organization contacts block (KAN-742)
 *
 * Six contact fields of the organization. The backend puts the filled ones into
 * the "КОНТАКТЫ" section of the bot prompt (registry ai_agent/prompt/contacts.py),
 * so the field list here mirrors that registry order.
 */
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type {
  OrganizationContactField,
  OrganizationSettings as OrgSettingsType,
} from '@/types/settings';
import { CONTACT_INPUTS, CONTACTS_HINT } from '@/lib/organizationContacts';

interface OrganizationContactsProps {
  settings: OrgSettingsType;
  onChange: (updates: Partial<OrgSettingsType>) => void;
  disabled: boolean;
}

/** Testid derived from the field name: company_email -> contact-company-email-input. */
function testIdFor(field: OrganizationContactField): string {
  return `contact-${field.replace(/_/g, '-')}-input`;
}

export function OrganizationContacts({ settings, onChange, disabled }: OrganizationContactsProps) {
  return (
    <div data-testid="org-contacts" className="space-y-3">
      <div>
        <Label>Контакты</Label>
        <p className="text-xs text-gray-500 mt-1">{CONTACTS_HINT}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CONTACT_INPUTS.map(({ field, label, placeholder, type }) => {
          const id = `org-contact-${field}`;
          return (
            <div key={field} className="space-y-2">
              <Label htmlFor={id}>{label}</Label>
              <Input
                id={id}
                data-testid={testIdFor(field)}
                type={type ?? 'text'}
                value={settings?.[field] || ''}
                onChange={(e) => onChange({ [field]: e.target.value })}
                disabled={disabled}
                placeholder={placeholder}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
